import { message, Tag, Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import CryptoJS from "crypto-js";
import { useRef, useState } from "react";
import dayjs from "dayjs";

const { confirm } = Modal;

export const encrypt = (data, salt) => {
  return CryptoJS.AES.encrypt(JSON.stringify(data), salt).toString();
};

export const decrypt = (ciphertext, salt) => {
  const bytes = CryptoJS.AES.decrypt(ciphertext, salt);
  try {
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch (err) {
    return null;
  }
};

export const getCrypto = () => {
  const salt = process.env.REACT_APP_SALT_KEY;
  const enc = (data) => encrypt(data, salt);
  const dec = (ciphertext) => decrypt(ciphertext, salt);
  return { encrypt: enc, decrypt: dec };
};

export const useNewState = (defaultValue) => {
  const [state, setNewState] = useState(defaultValue);
  const setState = (newState) =>
    // if (Array.isArray(newState)) {
    //   return setNewState((prevState) => [...prevState, ...newState]);
    // }
    setNewState((prevState) => ({ ...prevState, ...newState }));
  return [state, setState];
};

export const useFocus = () => {
  const inputRef = useRef(null);
  const setFocus = () => {
    if (inputRef.current) inputRef.current.focus();
  };
  return [inputRef, setFocus];
};

export const keyboardEvents = (onClickFunction) =>
  onClickFunction && typeof onClickFunction === "function"
    ? {
        tabIndex: 0,
        onClick: onClickFunction,
        onKeyDown: (e) => {
          if ([" ", "Enter"].includes(e.key)) {
            e.preventDefault();
            onClickFunction(e);
          }
        },
      }
    : {};

export const getGridify = (
  pagination,
  filters = [],
  sorter = {},
  defaultSort = []
) => {
  const values = [];
  Object.keys(filters).forEach((key) => {
    const vals = filters[key];
    if (vals && vals.length > 0) {
      const { operator, value } = vals[0];
      const hasRange = vals.length > 0 && ["><", "<>"].includes(operator);
      if (hasRange) {
        const { value: val2 } = vals[1];
        if (operator === "><") {
          // Between
          values.push(`(${key}>=${value},${key}<=${val2})`);
        } else {
          // Not between
          values.push(`(${key}<${value}|${key}>${val2})`);
        }
      } else {
        values.push(`${key}${operator}${value}`);
      }
    }
  });
  // handling multiple sorting fields with orders
  const sortOrders = [];
  defaultSort.forEach((s) => {
    sortOrders.push(`${s.field} ${s.order === "ascend" ? "asc" : "desc"}`);
  });
  const defaultSortOrder = sortOrders.join(", ");
  const sortOrder = `${sorter.field} ${
    sorter.order === "ascend" ? "asc" : "desc"
  }`;

  const gridify = {
    pageSize: pagination.pageSize,
    page: pagination.current,
    orderBy: sorter.field ? sortOrder : defaultSortOrder,
    filter: values.join(","),
  };
  return gridify;
};

export const useLayoutBreaks = () => ({
  xs: 24,
  sm: 24,
  md: 12,
  lg: 8,
  xl: 6,
  xxl: 6,
});

export const getStatus = (active) => {
  const color = active ? "green" : "red";
  return <Tag color={color}>{active ? "Active" : "Inactive"}</Tag>;
};

export const showConfirm = (msg, title = "Do you want to delete?") =>
  new Promise((resolve) => {
    confirm({
      icon: <ExclamationCircleOutlined />,
      title,
      content: msg,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        return resolve();
      },
      onCancel() {},
    });
  });

export const showSuccess = (msg) =>
  new Promise((resolve) => {
    message.success({
      content: msg,
      duration: 1.2,
      onClose: () => {
        resolve();
      },
    });
  });

export const showError = (msg) =>
  new Promise((resolve) => {
    message.error({
      content: msg,
      duration: 1.2,
      onClose: () => {
        resolve();
      },
    });
  });

export const showMessage = (success, msg) =>
  success ? showSuccess(msg) : showError(msg);

export const searchTree = (
  tree,
  keyField,
  childField,
  value,
  reverse = false
) => {
  const stack = [tree[0]];
  while (stack.length) {
    const node = stack[reverse ? "pop" : "shift"]();
    if (node) {
      if ((node[keyField] || "").toLowerCase() === value.toLowerCase()) {
        return node;
      }
      if (node[childField]) {
        stack.push(...node[childField]);
      }
    }
  }
  return null;
};

export const findParents = (data, keyField, childField, key) => {
  const treeIndex = {};
  const buildIndex = (root, children) => {
    if (children) {
      const entries = Object.entries(children);
      for (let i = 0; i < entries.length; i++) {
        if (root) {
          treeIndex[children[i][keyField]] = root;
        }
        buildIndex(children[i][keyField], children[i][childField]);
      }
    }
  };
  buildIndex(null, data);

  function getPath(leaf) {
    return treeIndex[leaf] ? getPath(treeIndex[leaf]).concat([leaf]) : [leaf];
  }
  return getPath(key);
};

export const formatDate = (dt, format = "MM/DD/YYYY") => {
  if (!dt) return "";
  return dayjs(dt).format(format);
};

export const getStore = (key, defaultValue) => {
  const value = localStorage.getItem(key);
  return value ? JSON.parse(value) : defaultValue;
};
export const setStore = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};
